import { apiRequest } from './httpClient';

// Same apiRequest used by every other service (authApi.js, meetingApi.js)
// - same base URL, same credentials/cookie behavior, same 401 refresh-
// and-retry handling. No separate admin HTTP configuration.
function toQueryString(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });

  const query = search.toString();
  return query ? `?${query}` : '';
}

export function getAdminDashboard(params) {
  return apiRequest(`/api/admin/dashboard${toQueryString(params)}`);
}

export function getAdminSystemStatus() {
  return apiRequest('/api/admin/system');
}

export function getDeploymentStatus() {
  return apiRequest('/api/admin/deployment');
}

export function updateDeploymentControl(data) {
  return apiRequest('/api/admin/deployment', { method: 'PATCH', body: data });
}

// params: { search, role, status, include_deleted, page, page_size }
// Soft-deleted users are hidden unless include_deleted=true (show them
// alongside active ones) or status='deleted' (show only them).
export function getAdminUsers(params) {
  return apiRequest(`/api/admin/users${toQueryString(params)}`);
}

export function getAdminUser(userId) {
  return apiRequest(`/api/admin/users/${userId}`);
}

// The user's meeting credit ledger, newest first: every meeting granted
// (plan periods, the free trial, passes) and every meeting spent, given
// back or refunded. Read-only.
export function getUserCreditLedger(userId, { limit = 20, offset = 0 } = {}) {
  return apiRequest(`/api/admin/users/${userId}/credit-ledger${toQueryString({ limit, offset })}`);
}

export function updateAdminUserStatus(userId, data) {
  return apiRequest(`/api/admin/users/${userId}/status`, {
    method: 'PATCH',
    body: data,
  });
}

export function updateAdminUserRole(userId, data) {
  return apiRequest(`/api/admin/users/${userId}/role`, {
    method: 'PATCH',
    body: data,
  });
}

// data: { plan_id, billing_cycle, reason }. plan_id always comes from a
// plan the caller loaded via getAdminPricingPlans() - never a typed
// plan name.
export function assignUserPlan(userId, data) {
  return apiRequest(`/api/admin/users/${userId}/subscription`, {
    method: 'PATCH',
    body: data,
  });
}

export function revokeAdminUserSessions(userId) {
  return apiRequest(`/api/admin/users/${userId}/revoke-sessions`, {
    method: 'POST',
  });
}

// Soft delete ("Move to Trash"): the account is marked deleted,
// disabled and its sessions revoked - the row and all of the user's
// history stay in the database. `reason` is optional free text stored
// against the deletion and in the audit log; it travels as a query
// parameter because DELETE request bodies are unevenly supported.
export function deleteAdminUser(userId, reason) {
  return apiRequest(
    `/api/admin/users/${userId}${toQueryString({ reason })}`,
    { method: 'DELETE' }
  );
}

// PERMANENT delete: removes the user and all of their data, no restore.
// confirmEmail must equal the user's email (checked server-side too).
export function hardDeleteAdminUser(userId, confirmEmail, reason) {
  return apiRequest(
    `/api/admin/users/${userId}/permanent${toQueryString({
      confirm_email: confirmEmail,
      reason,
    })}`,
    { method: 'DELETE' }
  );
}

export function restoreAdminUser(userId) {
  return apiRequest(`/api/admin/users/${userId}/restore`, { method: 'POST' });
}

// params: { user_id, status, date_from, date_to, stt_provider,
// tts_provider, translation_provider, source_language, target_language,
// audio_source, include_deleted, page, page_size }
export function getAdminMeetings(params) {
  return apiRequest(`/api/admin/meetings${toQueryString(params)}`);
}

export function getAdminMeeting(meetingId) {
  return apiRequest(`/api/admin/meetings/${meetingId}`);
}

export function getAdminMeetingContent(meetingId) {
  return apiRequest(`/api/admin/meetings/${meetingId}/content`);
}

// Soft delete - hides the meeting from its owner's history and from
// the default admin list. Transcripts and AI responses are untouched.
export function deleteAdminMeeting(meetingId, reason) {
  return apiRequest(
    `/api/admin/meetings/${meetingId}${toQueryString({ reason })}`,
    { method: 'DELETE' }
  );
}

export function restoreAdminMeeting(meetingId) {
  return apiRequest(`/api/admin/meetings/${meetingId}/restore`, {
    method: 'POST',
  });
}

// params: { action, admin_user_id, target_type, target_id, date_from, date_to, page, page_size }
export function getReleaseNotifications(params) {
  return apiRequest(`/api/admin/release-notifications${toQueryString(params)}`);
}

export function sendReleaseTestEmail(version) {
  return apiRequest('/api/admin/release-notifications/test-email', {
    method: 'POST',
    body: { version },
  });
}

export function getAdminAuditLogs(params) {
  return apiRequest(`/api/admin/audit${toQueryString(params)}`);
}

// Pricing plan management. The PUBLIC, unauthenticated view of the same
// data is services/pricingApi.js's getPublicPricing() - this admin list
// additionally includes DISABLED plans, which the public one never does.
// params: { include_deleted }. Disabled plans are ALWAYS included (an
// admin has to see one to re-enable it); trashed plans need
// include_deleted=true.
export function getAdminPricingPlans(params) {
  return apiRequest(`/api/admin/pricing${toQueryString(params)}`);
}

export function createAdminPricingPlan(data) {
  return apiRequest('/api/admin/pricing', { method: 'POST', body: data });
}

// PUT is a PARTIAL update on the backend (exclude_unset), so sending
// just { is_active: false } from the plan list never rewrites the
// plan's price or features as a side effect.
export function updateAdminPricingPlan(planId, data) {
  return apiRequest(`/api/admin/pricing/${planId}`, { method: 'PUT', body: data });
}

// Soft delete - the plan leaves the public catalogue and stops taking
// new subscribers, but the row stays so existing subscriptions keep
// resolving to real historical plan data. A plan with subscribers is
// deletable now (the old PLAN_IN_USE refusal only existed because the
// delete used to be permanent).
export function deleteAdminPricingPlan(planId, reason) {
  return apiRequest(
    `/api/admin/pricing/${planId}${toQueryString({ reason })}`,
    { method: 'DELETE' }
  );
}

export function restoreAdminPricingPlan(planId) {
  return apiRequest(`/api/admin/pricing/${planId}/restore`, { method: 'POST' });
}

// A user's own plan-change request, held PENDING until reviewed here -
// never an immediate subscription change. params: { page, page_size }.
export function getSubscriptionChangeRequests(params) {
  return apiRequest(`/api/admin/subscription-change-requests${toQueryString(params)}`);
}

export function approveSubscriptionChangeRequest(requestId) {
  return apiRequest(
    `/api/admin/subscription-change-requests/${requestId}/approve`,
    { method: 'POST' }
  );
}

export function rejectSubscriptionChangeRequest(requestId, reason) {
  return apiRequest(
    `/api/admin/subscription-change-requests/${requestId}/reject`,
    { method: 'POST', body: { reason } }
  );
}

// -------------------------------------------------------------- billing ----

// All-time captured revenue, net of refunds - by_currency (never
// summed across currencies) and by_plan. Computed from this app's own
// payments ledger, never fetched live from Razorpay.
export function getAdminRevenueSummary() {
  return apiRequest('/api/admin/billing/revenue');
}

// params: { limit, offset, status }. Every payment across every user,
// newest first - unlike billingApi.js's getPaymentHistory(), which is
// scoped to the signed-in user's own history.
export function getAdminPayments(params) {
  return apiRequest(`/api/admin/billing/payments${toQueryString(params)}`);
}

// params: { limit, offset }. Read-only: payment_subscriptions rows the
// provider currently considers live/chargeable that never produced an
// entitlement row - the exact condition runAdminRecoverPendingGrants()
// below acts on. Viewing this list makes no provider calls.
export function getAdminOrphanedSubscriptions(params) {
  return apiRequest(
    `/api/admin/billing/orphaned-subscriptions${toQueryString(params)}`
  );
}

// params: { limit }. Webhook events that arrived but never completed -
// the replay queue.
export function getAdminUnprocessedWebhooks(params) {
  return apiRequest(`/api/admin/billing/webhooks/unprocessed${toQueryString(params)}`);
}

// Re-dispatches one already-stored, already-verified webhook event from
// its stored payload. Never accepts a body - the admin supplies only
// the event's own id.
export function replayAdminWebhook(webhookEventId) {
  return apiRequest(`/api/admin/billing/webhooks/${webhookEventId}/replay`, {
    method: 'POST',
  });
}

// params: { min_age_minutes }. Runs the first-grant recovery sweep now,
// instead of waiting for the next scheduled interval. Re-checks the
// provider before granting anything - never trusts a local row alone.
export function runAdminRecoverPendingGrants(params) {
  return apiRequest(
    `/api/admin/billing/recover-pending-grants${toQueryString(params)}`,
    { method: 'POST' }
  );
}

// ---- API credit monitoring (platform OpenAI / Sarvam prepaid credit) ----
// Read-only towards the providers: nothing here buys credit. A "top-up"
// only RECORDS credit an admin already bought on the provider's site.

export function getApiCreditOverview() {
  return apiRequest('/api/admin/api-credits');
}

// data: { provider, enabled?, low_balance_threshold?, monitor_interval_minutes?, recipient_emails? }
export function updateApiCreditSettings(data) {
  return apiRequest('/api/admin/api-credits/settings', { method: 'PATCH', body: data });
}

// data: { balance, currency, snapshot_at?, notes?, idempotency_key }
export function recordApiCreditSnapshot(provider, data) {
  return apiRequest(`/api/admin/api-credits/${provider}/snapshots`, { method: 'POST', body: data });
}

// data: { entry_type: 'top_up'|'credit_adjustment'|'debit_adjustment', amount, currency, effective_at?, notes?, idempotency_key }
export function recordApiCreditAdjustment(provider, data) {
  return apiRequest(`/api/admin/api-credits/${provider}/adjustments`, { method: 'POST', body: data });
}

export function checkApiCreditsNow(provider) {
  return apiRequest('/api/admin/api-credits/check-now', {
    method: 'POST',
    body: provider ? { provider } : {},
  });
}

export function sendApiCreditTestEmail(provider) {
  return apiRequest('/api/admin/api-credits/send-test-email', {
    method: 'POST',
    body: { provider },
  });
}

export function getApiCreditAlerts(params) {
  return apiRequest(`/api/admin/api-credits/alerts${toQueryString(params)}`);
}

export function resolveApiCreditAlert(incidentId) {
  return apiRequest(`/api/admin/api-credits/alerts/${incidentId}/resolve`, { method: 'POST' });
}
